import { useEffect, useRef } from 'react';

/**
 * Escape closes the innermost open layer, and only that one.
 *
 * Capture-phase listeners are not enough on their own: every layer registers
 * on `document`, and `stopPropagation` does nothing between listeners on the
 * same node - so whichever registered first won, which is the outermost. A
 * dropdown inside a drawer therefore shut the drawer, losing whatever had been
 * typed into the form behind it.
 *
 * So the layers keep a stack instead. One listener, dispatching to the top of
 * it: the dropdown closes, the drawer stays, and a second Escape closes the
 * drawer. Open layers are pushed in mount order, which for nested overlays is
 * outermost first, because an inner one cannot exist before its container.
 */
const layers: (() => void)[] = [];

let listening = false;

function onKeyDown(event: KeyboardEvent): void {
  if (event.key !== 'Escape') {
    return;
  }

  const top = layers.at(-1);

  if (!top) {
    return;
  }

  /* Stops a native dialog or a browser gesture from acting on the same
     keystroke that just closed our own layer. */
  event.stopPropagation();
  event.preventDefault();
  top();
}

export function useEscapeKey(handler: () => void, enabled = true): void {
  /* Held in a ref so a caller passing an inline closure does not re-register -
     and therefore does not jump to the top of the stack - on every render. */
  const latest = useRef(handler);

  useEffect(() => {
    latest.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const layer = (): void => {
      latest.current();
    };

    layers.push(layer);

    if (!listening) {
      document.addEventListener('keydown', onKeyDown, true);
      listening = true;
    }

    return () => {
      const index = layers.indexOf(layer);

      if (index >= 0) {
        layers.splice(index, 1);
      }

      if (layers.length === 0 && listening) {
        document.removeEventListener('keydown', onKeyDown, true);
        listening = false;
      }
    };
  }, [enabled]);
}
